/**
 * AnalyticsChartTabs — Tabbed container for Price, Volume, and Holder charts.
 *
 * Default tab: "Price" (preserves existing behavior).
 * Lazy renders: only the active chart is mounted.
 *
 * Story 8.3 — Task 4
 */

import { useState } from "react";
import { HolderChart } from "./HolderChart";
import { PriceChart } from "./PriceChart";
import { VolumeChart } from "./VolumeChart";

type AnalyticsChartTabsProps = {
	tokenId: string;
};

type ChartTab = "price" | "volume" | "holders";

const TABS: Array<{ value: ChartTab; label: string }> = [
	{ value: "price", label: "Price" },
	{ value: "volume", label: "Volume" },
	{ value: "holders", label: "Holders" },
];

export function AnalyticsChartTabs({ tokenId }: AnalyticsChartTabsProps) {
	const [activeTab, setActiveTab] = useState<ChartTab>("price");

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-4 space-y-4">
			<div className="flex gap-1" role="tablist" aria-label="Analytics charts">
				{TABS.map((tab) => (
					<button
						key={tab.value}
						type="button"
						role="tab"
						aria-selected={activeTab === tab.value}
						aria-controls={activeTab === tab.value ? `chart-panel-${tab.value}` : undefined}
						onClick={() => setActiveTab(tab.value)}
						className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
							activeTab === tab.value
								? "bg-pastel-primary text-white"
								: "text-pastel-muted hover:text-pastel-black hover:bg-pastel-surface"
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			<div id={`chart-panel-${activeTab}`} role="tabpanel" aria-label={`${activeTab} chart`}>
				{activeTab === "price" && <PriceChart tokenId={tokenId} />}
				{activeTab === "volume" && <VolumeChart tokenId={tokenId} />}
				{activeTab === "holders" && <HolderChart tokenId={tokenId} />}
			</div>
		</div>
	);
}
